import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import './SessionTable.css';
import { Avatar, AvatarGroup, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import OneOnOneSessionIcon from '../../assets//Dashboard/oneoneonesession.svg';
import GroupSessionIcon from '../../assets//Dashboard/group.svg';
import PrimeSessionIcon from '../../assets//Dashboard/prime.svg';
import { useTranslation } from '../../i18n/LanguageContext';

const SessionTypeColor = {
  one_on_one: {
    color: '#0A5EF3',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: {
    color: '#7BCF8C',
    bgcolor: '#FAFFFB',
    value: 'Group',
    icon: GroupSessionIcon,
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#F0EFFF',
    value: 'Prime',
    icon: PrimeSessionIcon,
  },
};

function formatText(input) {
  if (!input) return '---';
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

export default function SessionTable({ sessionData }) {
  const navigate = useNavigate();
  const { translate } = useTranslation();

  const handleCellClick = (event, id) => {
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      navigate(`/instructor/sessions-details/${id}`);
    }
  };

  const formatTimeInterval = (startTime, lengthInMinutes) => {
    if (!startTime || !lengthInMinutes) return '---';

    const start = dayjs(startTime);
    const end = dayjs(startTime).add(lengthInMinutes, 'minutes');

    // Format start and end times
    const startFormat = start.format('h:mm a');
    const endFormat = end.format('h:mm a');

    // Return formatted time range
    return `${startFormat} - ${endFormat}`;
  };

  // Format date as "Mon 7 Dec, 2024"
  const formatDate = dateString => {
    if (!dateString) return '---';
    return dayjs(dateString).format('ddd D MMM, YYYY');
  };

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table sx={{ minWidth: 650 }} aria-label="session table">
        <TableHead
          sx={{
            backgroundColor: '#EEF4FF',
            '& th': {
              color: 'var(--Text, #14171F)',
              height: '40px',
              fontFamily: 'Poppins',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: '600',
              lineHeight: 'normal',
              padding: '12px 16px',
            },
          }}
        >
          <TableRow>
            <TableCell align="left">{translate('session', 'studentNameText')}</TableCell>
            <TableCell align="left">{translate('session', 'dateText')}</TableCell>
            <TableCell align="left">{translate('session', 'timingText')}</TableCell>
            <TableCell>{translate('session', 'subjectText')}</TableCell>
            <TableCell align="left">Levels</TableCell>
            <TableCell align="left">{translate('session', 'sessionTypeText')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            '& td': {
              color: 'var(--Text, #14171F) !important',
              height: '40px !important',
              fontFamily: 'Poppins !important',
              fontSize: '14px !important',
              fontStyle: 'normal !important',
              fontWeight: '400 !important',
              lineHeight: 'normal !important',
              padding: '14px 16px',
            },
          }}
        >
          {sessionData?.length > 0 ? (
            sessionData.map(row => {
              return (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  className="session-table-row"
                  sx={{
                    '&:hover': {
                      backgroundColor: '#E9F1FF !important',
                      cursor: 'pointer',
                    },
                  }}
                >
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    <Box className="flex items-center gap-4">
                      {row.session_type === 'one_on_one' ? (
                        // Single student display for one-on-one sessions
                        <Box className="flex items-center gap-4">
                          <Avatar
                            alt={row.students[0]?.user?.name || 'Student'}
                            src={row.students[0]?.user?.profile_image || ''}
                            sx={{
                              width: 25,
                              height: 25,
                              fontSize: '0.75rem',
                              backgroundColor: 'grey.300',
                              color: 'grey.700',
                            }}
                          />
                          <Typography>{formatText(row.students[0]?.user?.name)}</Typography>
                        </Box>
                      ) : (
                        // Multiple student display for group and prime sessions
                        <Box className="flex items-center gap-4">
                          <AvatarGroup
                            max={4}
                            total={row.students?.length || 0}
                            sx={{
                              '& .MuiAvatar-root': {
                                width: 25,
                                height: 25,
                              },
                              '& .MuiAvatarGroup-avatar': {
                                width: 25,
                                height: 25,
                                fontSize: '0.75rem',
                                backgroundColor: 'grey.300',
                                color: 'grey.700',
                              },
                            }}
                          >
                            {row.students?.map(student => (
                              <Avatar
                                key={student?.id}
                                alt={student?.user?.name || ''}
                                src={student?.user?.profile_image}
                              />
                            ))}
                          </AvatarGroup>
                          <Typography>
                            {formatText(row.students[0]?.user?.name)}
                            {row.students.length > 1 && ` +${row.students.length - 1}`}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatDate(row?.start_time)}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatTimeInterval(row?.start_time, row?.length_in_minutes)}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {row?.subject || '---'}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatText(row?.level_of_teaching)}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    <Box
                      style={{
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        color: SessionTypeColor[row?.session_type]?.color,
                        fontFamily: 'Poppins !important',
                        fontSize: '14px !important',
                        fontStyle: 'normal !important',
                        fontWeight: '400 !important',
                        lineHeight: 'normal !important',
                      }}
                    >
                      <img
                        src={SessionTypeColor[row?.session_type]?.icon}
                        alt={SessionTypeColor[row?.session_type]?.value}
                      />
                      <Typography className="level-text">
                        {row?.session_type ? SessionTypeColor[row?.session_type]?.value : '---'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                <Typography>No data found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
